import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { withQuery } from '@/api/client';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { Empty } from '@/components/States';
import { ViewSidebar, type SidebarView } from '@/components/ViewSidebar';
import { money, num, qty } from '@/lib/format';
import { AppliedChips, CardList, FilterButton, ListBody, ListScaffold, Pager, SearchBox, Segments, ViewChips } from './kit/List';
import { FilterSheet } from './kit/Sheet';
import { DataTable, Two, type Col } from './kit/Table';
import { Thumb, productCover } from './kit/Thumb';
import { useInfinite, usePagedList, useResource, useUrlState, withParam } from './kit/hooks';
import { ProductCard } from './Cards';
import { CategorySheet } from './CategorySheet';
import { ProductDetail } from './ProductDetail';
import { useCatalogSegments } from './nav';
import { defaultPackaging, defaultQuote, packagingLabel, perPackage } from './rules';
import type { ProductListItem, ProductStats } from './types';
import { ProductImportDialog } from './ProductImportDialog';
import { exportProducts } from './productTransfer';
import s from './ProductsPage.module.css';

/** 视图 = 老接口的 inventory / packaging 单个参数（src/app/api/products/route.ts productWhereSql） */
const VIEWS: { key: string; label: string; query: Record<string, string>; count: (st: ProductStats) => number }[] = [
  { key: 'all', label: '全部产品', query: {}, count: (st) => st.all },
  { key: 'inStock', label: '有库存', query: { inventory: 'inStock' }, count: (st) => st.inStock },
  { key: 'reserved', label: '有预占', query: { inventory: 'reserved' }, count: (st) => st.reserved },
  { key: 'shortage', label: '负可用', query: { inventory: 'shortage' }, count: (st) => st.shortage },
  { key: 'noStock', label: '零库存', query: { inventory: 'noStock' }, count: (st) => st.noStock },
  { key: 'noPackaging', label: '无包装方案', query: { packaging: 'withoutPackaging' }, count: (st) => Math.max(st.all - st.withPackaging, 0) },
  { key: 'noQuote', label: '无供应商报价', query: {}, count: () => -1 },
  { key: 'noImage', label: '无图片', query: {}, count: () => -1 },
];

const UNCATEGORIZED = '__uncategorized__';
const catLabel = (v: string) => (v === UNCATEGORIZED ? '未分类' : v);

export function ProductsPage() {
  const can = useSession((st) => st.can);
  if (!can('products.read')) {
    return can('gallery.read') ? <Navigate to="/gallery" replace /> : <Empty icon="lock" title="没有查看产品资料的权限">需要「产品资料 · 查看」权限，请联系管理员。</Empty>;
  }
  return <ProductList />;
}

function ProductList() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const segments = useCatalogSegments();
  const [sp, set] = useUrlState();
  const view = VIEWS.find((v) => v.key === sp.get('view')) || VIEWS[0];
  const flagView = view.key === 'noQuote' || view.key === 'noImage';
  const cat = sp.get('cat') || undefined;
  const q = sp.get('q') || '';
  const requestedId = sp.get('id') || undefined;
  const [panel, setPanel] = useState<'filter' | 'cats' | 'import' | null>(null);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [transferMessage, setTransferMessage] = useState('');
  const exportController = useRef<AbortController | null>(null);
  const restrictions = useSession((st) => st.user?.restrictions);
  useEffect(() => () => exportController.current?.abort(), []);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const desk = ff === 'desktop';
  const query = useMemo(() => ({ ...view.query, view: flagView ? view.key : undefined, category: cat, keyword: q || undefined }), [view, flagView, cat, q]);
  const list = usePagedList<ProductListItem, ProductStats>(flagView ? '/api/v2/products/flags' : '/api/products', query, desk ? 30 : 20, !desk, !flagView);
  // iPad 的左右分栏原稿默认显示首个产品详情；用列表派生选择，避免异步加载后的状态 effect 和多余 URL 写入。
  const selectedId = requestedId || (ff === 'tablet' ? list.items[0]?.id : undefined);
  const baseStats = useResource<{ stats: ProductStats }>(flagView ? withQuery('/api/products', { pageSize: 1, includeStats: 1, keyword: q, category: cat }) : null);
  const flagCounts = useResource<{ noQuote: number; noImage: number }>(withQuery('/api/v2/products/flags', { meta: 'counts', keyword: q, category: cat }));
  useInfinite(sentinel, !desk, list.loadMore, list.items.length);
  const currentPageIds = list.items.map((p) => p.id);
  const selectedOnPage = currentPageIds.filter((id) => selectedIds.includes(id)).length;
  const allOnPageSelected = currentPageIds.length > 0 && selectedOnPage === currentPageIds.length;
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selectedOnPage > 0 && !allOnPageSelected;
  }, [selectedOnPage, allOnPageSelected]);
  const inventoryValue = list.page === 1 && !list.hasNext && !list.loading
    ? list.items.reduce((total, product) => total + num(product.inventorySummary.inventoryValue), 0)
    : undefined;

  const stats = flagView ? baseStats.data?.stats : list.stats;
  const viewCount = (key: string) => key === 'noQuote' ? flagCounts.data?.noQuote : key === 'noImage' ? flagCounts.data?.noImage : undefined;
  const filtered = !!(q || cat);
  const cols: Col<ProductListItem>[] = [
    { key: 'select', title: <input ref={selectAllRef} className={s.check} type="checkbox" checked={allOnPageSelected} aria-label="选择当前页全部产品" onClick={(event) => event.stopPropagation()} onChange={(event) => setSelectedIds((current) => event.target.checked ? Array.from(new Set([...current, ...currentPageIds])) : current.filter((id) => !currentPageIds.includes(id)))} />, w: 44, fix: true, render: (p) => <input className={s.check} type="checkbox" checked={selectedIds.includes(p.id)} aria-label={`选择${p.name}`} onClick={(event) => event.stopPropagation()} onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, p.id] : current.filter((id) => id !== p.id))} /> },
    { key: 'img', title: '图', w: 56, fixLeft: 44, render: (p) => <Thumb src={productCover(p)} alt={p.name} size={36} fallbackIcon="box" /> },
    { key: 'name', title: '产品', render: (p) => <Two top={<b>{p.name}</b>} sub={p.code} /> },
    { key: 'cat', title: '分类', w: 112, render: (p) => p.category || <span className="em">未分类</span> },
    {
      key: 'pk', title: '默认包装 / 供应商', render: (p) => {
        const pk = defaultPackaging(p.packagingOptions);
        if (!pk) return <span className={s.warn}>未设包装方案</span>;
        const qt = defaultQuote(pk);
        return <Two top={perPackage(pk) || packagingLabel(pk)} sub={qt ? `${qt.supplier?.name || '未命名供应商'} ${money(qt.quotePrice)}` : '无供应商报价'} />;
      },
    },
    { key: 'actual', title: '现有', w: 104, align: 'r', render: (p) => qty(p.inventorySummary.actualQuantity) },
    { key: 'reserved', title: '预占', w: 104, align: 'r', render: (p) => qty(p.inventorySummary.reservedQuantity) },
    { key: 'available', title: '可用', w: 104, align: 'r', render: (p) => <span className={Number(p.inventorySummary.availableQuantity) < 0 ? s.negative : undefined}>{qty(p.inventorySummary.availableQuantity)}</span> },
    { key: 'salePrice', title: '销售参考价', w: 116, align: 'r', render: (p) => money(p.salePrice) },
    { key: 'actions', title: '操作', w: 120, render: (p) => <div className={s.rowActions}>
      <button type="button" className={s.rowIcon} title={`查看${p.name}`} aria-label={`查看${p.name}`} onClick={(event) => { event.stopPropagation(); nav(`/products/${p.id}`); }}><Icon name="eye" size={16} /></button>
      {can('products.write') && <button type="button" className={s.rowIcon} title={`${p.name}包装方案与报价`} aria-label={`${p.name}包装方案与报价`} onClick={(event) => { event.stopPropagation(); nav(`/products/${p.id}?panel=packaging`); }}><Icon name="inbox" size={16} /></button>}
      {can('products.write') && <button type="button" className={s.rowIcon} title={`删除或停用${p.name}`} aria-label={`删除或停用${p.name}`} onClick={(event) => { event.stopPropagation(); nav(`/products/${p.id}?status=1`); }}><Icon name="trash" size={16} /></button>}
    </div> },
  ];

  const bodyState = { loading: list.loading, error: list.error, count: list.items.length };
  const empty = (
    <Empty icon="box" title={filtered ? '没有符合条件的产品' : `「${view.label}」里没有产品`}>
      {filtered ? '换个关键词，或者清空筛选再看看。' : '换个视图看看。'}
    </Empty>
  );
  const common = { state: bodyState, errorTitle: '产品列表加载失败', detail: 'GET /api/products', onRetry: list.reload, empty };

  const cards = (
    <ListBody {...common}>
      <CardList bar={<span>{list.total} 个产品{q && ` · 搜索「${q}」`}</span>} hasNext={list.hasNext} loading={list.loading} count={list.items.length} sentinel={setSentinel}>
        {list.items.map((p) => (
          <ProductCard key={p.id} p={p} selected={ff === 'tablet' && p.id === selectedId} to={ff === 'tablet' ? `/products?${withParam(sp, 'id', p.id)}` : `/products/${p.id}`} />
        ))}
      </CardList>
    </ListBody>
  );

  const runExport = async () => {
    if (exporting) return;
    const controller = new AbortController(); exportController.current = controller;
    setExporting(true); setTransferMessage('');
    try {
      const count = await exportProducts(flagView ? '/api/v2/products/flags' : '/api/products', query, controller.signal, !restrictions?.includes('不能查看产品成本与入库均价'));
      if (!controller.signal.aborted) setTransferMessage(`已导出当前筛选的 ${count} 个产品`);
    } catch (cause) { if (!controller.signal.aborted) setTransferMessage(cause instanceof Error ? cause.message : '导出失败'); }
    finally { if (!controller.signal.aborted) setExporting(false); }
  };
  const transferActions = <div className={s.toolbarActions}>{can('products.write') && <button type="button" className={s.toolbarAction} onClick={() => setPanel('import')}>导入</button>}<button type="button" className={s.toolbarAction} disabled={exporting} onClick={() => void runExport()}>{exporting ? '导出中…' : '导出'}</button>{transferMessage && <span role="status">{transferMessage}</span>}</div>;
  const overlay =
    panel === 'import' ? <ProductImportDialog onClose={() => setPanel(null)} onImported={list.reload} /> : panel === 'filter' ? (
      <FilterSheet
        value={{ cat }}
        groups={[{
          key: 'cat',
          title: '分类',
          opts: (stats?.categories || []).map((c) => ({ value: c.value, label: c.label, count: c.count })),
          extra: <button type="button" className={s.link} onClick={() => setPanel('cats')}>分类列表<Icon name="chev" size={14} /></button>,
        }]}
        hint="视图与分类、搜索条件可组合使用。"
        onClose={() => setPanel(null)}
        onApply={(v) => { setPanel(null); set({ cat: v.cat, id: undefined }); }}
      />
    ) : panel === 'cats' ? (
      <CategorySheet counts={stats?.categories} onClose={() => setPanel(null)} onPick={(name) => { setPanel(null); set({ cat: name, id: undefined }); }} />
    ) : null;

  const sidebarViews: SidebarView[] = [
    { key: 'all', label: '全部产品', icon: 'box', count: stats?.all },
    { key: 'inStock', label: '有库存', icon: 'ok', count: stats?.inStock },
    { key: 'shortage', label: '负可用', icon: 'alert', count: stats?.shortage, hot: true },
    { key: 'noStock', label: '零库存', icon: 'flag', count: stats?.noStock },
    { key: 'noPackaging', label: '无包装方案', icon: 'inbox', count: stats ? Math.max(stats.all - stats.withPackaging, 0) : undefined, separator: true },
    { key: 'noQuote', label: '无供应商报价', icon: 'team', count: viewCount('noQuote') },
    { key: 'noImage', label: '无图片', icon: 'book', count: viewCount('noImage') },
    { key: 'gallery', label: '产品画册', icon: 'book', to: '/gallery', separator: true },
    { key: 'bundles', label: '产品套装', icon: 'dash', to: '/bundles' },
  ];

  return (
    <ListScaffold
      title={desk ? '产品管理' : '产品资料'}
      subtitle={desk ? <>产品是唯一的库存单位。套装在下单时展开成产品行，不单独记库存<span className={s.total}>共 {list.total} 个 · 库存总值 {inventoryValue === undefined ? '—' : money(inventoryValue)}</span></> : undefined}
      total={desk ? undefined : list.total}
      sidebar={<ViewSidebar title="产品视图" views={sidebarViews} active={view.key} onPick={(key) => set({ view: key === 'all' ? undefined : key, id: undefined })} />}
      segments={desk ? undefined : <Segments items={segments} />}
      chips={<ViewChips items={VIEWS.map((v) => ({ key: v.key, label: v.label, count: viewCount(v.key) ?? (stats && !['noQuote', 'noImage'].includes(v.key) ? v.count(stats) : undefined) }))} value={view.key} onChange={(k) => set({ view: k === 'all' ? undefined : k, id: undefined })} />}
      filterBtn={desk
        ? <><button type="button" className={s.categoryFilter} onClick={() => setPanel('filter')}>分类：{catLabel(cat || '全部')}<Icon name="chev" size={14} /></button>{transferActions}</>
        : <><FilterButton count={cat ? 1 : 0} onClick={() => setPanel('filter')} />{transferActions}</>}
      applied={<AppliedChips items={cat ? [{ key: 'cat', label: catLabel(cat) }] : []} onRemove={() => set({ cat: undefined })} onClear={() => set({ cat: undefined })} />}
      search={<SearchBox value={q} placeholder="产品名称 / 编号 / 包装 / 供应商" onSubmit={(v) => set({ q: v, id: undefined })} />}
      searching={!!q}
      create={can('products.write') ? { href: '/products/new', label: '新建产品', local: true } : null}
      actions={desk ? <><button type="button" className={s.headerAction} onClick={() => setPanel('cats')}>分类管理</button><button type="button" className={s.headerAction}><Icon name="cog2" size={14} />列设置</button></> : undefined}
      onRefresh={desk ? undefined : list.reload}
      list={cards}
      desk={
        <ListBody {...common}>
          <DataTable cols={cols} rows={list.items} onRow={(p) => nav(`/products/${p.id}`)} minWidth={1200} />
        </ListBody>
      }
      pager={desk && list.items.length > 0 ? <Pager page={list.page} size={list.pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGo={list.goto} /> : undefined}
      detail={
        ff === 'tablet'
          ? selectedId
            ? <ProductDetail key={selectedId} id={selectedId} embedded />
            : <Empty icon="cards" title="选择左侧产品查看详情">规格、包装方案与各仓库存在这里打开，不用跳页。</Empty>
          : undefined
      }
      overlay={overlay}
    />
  );
}
